import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { industryService } from '../../../services/industryService';
import ConfettiSelector from './ConfettiSelector';
import { getIndustryConfetti } from '../../../utils/industryConfettiConfig';
import * as LucideIcons from 'lucide-react';

export default function IndustryModal({ industry, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    naicsCode: '',
    description: '',
    icon: 'Briefcase',
    color: '#3b82f6',
    sortOrder: 0,
    confettiIcons: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfettiSelector, setShowConfettiSelector] = useState(false);

  useEffect(() => {
    if (industry) {
      // Deserialize confetti icons if they exist in the database
      let confettiIcons = [];
      if (industry?.confettiIcons && Array.isArray(industry?.confettiIcons) && industry?.confettiIcons?.length > 0) {
        // Check if already deserialized (has icon component)
        if (industry?.confettiIcons?.[0]?.icon && typeof industry?.confettiIcons?.[0]?.icon === 'function') {
          confettiIcons = industry?.confettiIcons;
        } else {
          // Deserialize from database format
          confettiIcons = industry?.confettiIcons?.map(iconData => {
            const IconComponent = LucideIcons?.[iconData?.iconName] || LucideIcons?.Circle;
            return {
              icon: IconComponent,
              color: iconData?.color,
              label: iconData?.label
            };
          });
        }
      } else {
        // Use default confetti for this industry
        confettiIcons = getIndustryConfetti(industry?.naicsCode) || [];
      }
      
      setFormData({
        name: industry?.name || '',
        naicsCode: industry?.naicsCode || '',
        description: industry?.description || '',
        icon: industry?.icon || 'Briefcase',
        color: industry?.color || '#3b82f6',
        sortOrder: industry?.sortOrder || 0,
        confettiIcons: confettiIcons
      });
    }
  }, [industry]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleConfettiSave = (selectedIcons) => {
    setFormData(prev => ({ ...prev, confettiIcons: selectedIcons }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.name?.trim()) {
      setError('Industry name is required');
      return;
    }

    if (!formData?.naicsCode?.trim()) {
      setError('NAICS code is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (industry) {
        await industryService?.update(industry?.id, formData);
      } else {
        await industryService?.create(formData);
      }
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save industry');
      console.error('Error saving industry:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {industry ? 'Edit Industry' : 'Add New Industry'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Industry Name *
              </label>
              <Input
                type="text"
                value={formData?.name}
                onChange={(e) => handleChange('name', e?.target?.value)}
                placeholder="e.g., HVAC & Plumbing, Landscaping"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                NAICS Code *
              </label>
              <Input
                type="text"
                value={formData?.naicsCode}
                onChange={(e) => handleChange('naicsCode', e?.target?.value)}
                placeholder="e.g., 238220, 561730"
                required
                disabled={loading}
                maxLength={10}
              />
              <p className="mt-1 text-xs text-gray-500">
                North American Industry Classification System code
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                placeholder="Brief description of this industry"
                rows={3}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Icon Name (Lucide)
              </label>
              <Input
                type="text"
                value={formData?.icon}
                onChange={(e) => handleChange('icon', e?.target?.value)}
                placeholder="e.g., Wrench, HardHat, Scissors"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">
                Use Lucide icon names (e.g., Wrench, HardHat, Scissors)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData?.color}
                  onChange={(e) => handleChange('color', e?.target?.value)}
                  disabled={loading}
                  className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  type="text"
                  value={formData?.color}
                  onChange={(e) => handleChange('color', e?.target?.value)}
                  placeholder="#3b82f6"
                  disabled={loading}
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <Input
                type="number"
                value={formData?.sortOrder}
                onChange={(e) => handleChange('sortOrder', parseInt(e?.target?.value) || 0)}
                placeholder="0"
                disabled={loading}
                min="0"
              />
              <p className="mt-1 text-xs text-gray-500">
                Lower numbers appear first in lists
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confetti Icons
              </label>
              <div className="flex items-center gap-2 mb-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowConfettiSelector(true)}
                  disabled={loading}
                  className="text-sm"
                >
                  Select Icons ({formData?.confettiIcons?.length || 0})
                </Button>
              </div>
              {formData?.confettiIcons?.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  {formData?.confettiIcons?.map((iconData, index) => {
                    const IconComponent = iconData?.icon;
                    return (
                      <div
                        key={`${iconData?.label}-${index}`}
                        className="flex items-center gap-1 bg-white px-2 py-1 rounded border border-gray-200"
                      >
                        <IconComponent
                          className="w-4 h-4"
                          style={{ color: iconData?.color }}
                          strokeWidth={2}
                        />
                        <span className="text-xs text-gray-600">{iconData?.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Select multiple icons to create a custom confetti mix
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : industry ? 'Update Industry' : 'Add Industry'}
              </Button>
            </div>
          </form>
        </div>
      </div>
      {showConfettiSelector && (
        <ConfettiSelector
          selectedIcons={formData?.confettiIcons || []}
          onSave={handleConfettiSave}
          onClose={() => setShowConfettiSelector(false)}
        />
      )}
    </>
  );
}